import { concat } from 'lodash';

import { EntityPickerSettingsFilterBy } from '@work4all/data/lib/settings/settings';

import {
  DataRequest,
  Filters,
  KeysArguments,
  SortDirection,
} from '@work4all/models/lib/DataProvider';
import { Filter } from '@work4all/models/lib/DataProvider/filters';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileData } from '@work4all/models/lib/File';

import { EntityId, EntityLike } from '../../types';

// Manipulation of Id is done to have more then 1 entity in the single picker (businessPartner)
function enrichId<T extends EntityLike>(id: EntityId, entity: T): EntityId {
  if (typeof entity === 'string') return entity;

  // Parsing to string to avoid different types, but same values as type is lost sometimes
  return `${id}`;
}

// Factory function
export function makeGetEntityId(type: Entities) {
  return function <T extends EntityLike>(entity: T): EntityId {
    const id = getEntityId(entity);
    return enrichId(id, entity);
  };
}

export function getEntityId<T extends EntityLike>(entity: T): EntityId {
  if (typeof entity === 'string') return entity;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return entity?.id ?? (entity as any)?.data?.id;
}

export function toSelectionValue<T>(value: T | T[] | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

export type IPrepareRequestOptions = {
  entity: Entities;
  query: string;
  prefilter: Filters;
  data: unknown;
  filterBy: string | string[];
  searchPrefilter?: Filters;
  settingsFilter?: Filter;
  sortBy: string;
  sortByDirection?: SortDirection;
  searchHistory: FileData[];
  skip: boolean;
  vars: Record<string, unknown>;
  showFavorites?: boolean;
  keysArguments?: KeysArguments;
  completeDataResponse: boolean;
};
export function prepareRequest({
  entity,
  query,
  prefilter,
  data,
  filterBy,
  searchPrefilter,
  settingsFilter,
  sortBy,
  sortByDirection = SortDirection.ASCENDING,
  searchHistory,
  skip,
  vars,
  showFavorites,
  keysArguments,
  completeDataResponse,
}: IPrepareRequestOptions): DataRequest {
  const initialPrefilter =
    searchPrefilter && query ? searchPrefilter : prefilter;
  const filter = showFavorites ? [] : initialPrefilter;

  const filterProperties = filterBy instanceof Array ? filterBy : [filterBy];
  const filterExpressions =
    query === ''
      ? []
      : filterProperties.map((field) => ({
          [field]: { $eq: `%${query}%` },
        }));
  const searchFilter: Filter =
    filterExpressions.length > 0 ? { $or: filterExpressions } : null;

  const historyFilter: Filter =
    searchHistory.length > 0 && query === '' && !showFavorites
      ? { id: { $in: searchHistory.map((item) => item.id) } }
      : null;

  const finalFilter: Filters = concat(
    ...([
      filter,
      !completeDataResponse && searchFilter,
      historyFilter,
      settingsFilter,
    ].filter(Boolean) as any) // TECH_DEBT
  );

  return {
    skip,
    entity,
    data,
    vars,
    sort: [{ field: sortBy, direction: sortByDirection }],
    filter: finalFilter,
    keysArguments,
    completeDataResponse,
  };
}

export function prepareSettingsFilters(
  settingsFilters?: EntityPickerSettingsFilterBy[],
  prefix?: string
) {
  if (!settingsFilters?.length) return null;
  return {
    $or: settingsFilters.map((settingFilter) => {
      const key = prefix ? `${prefix}.${settingFilter.key}` : settingFilter.key;
      return { [key]: settingFilter.query };
    }),
  };
}

export function filterOutById<T extends EntityLike>(
  list: T[],
  remove: T[]
): T[] {
  if (!remove.length) return list;

  const removeIds = new Set(remove.map((item) => getEntityId(item)));
  return list.filter((item) => !removeIds.has(getEntityId(item)));
}
